import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircleQuestionMark } from "lucide-react";
import { POPOVER_GAP, POPOVER_VIEWPORT_MARGIN } from "./usePopoverPlacement";

// 팝오버 fixed 좌표 — 가로 경계는 뷰포트 좌우 16px, 트리거가 모달([role="dialog"]) 안이고 팝오버가 들어가면 모달 좌우 16px.
//   선호 정렬(start=트리거 왼쪽 / end=트리거 오른쪽)로 두되 경계를 넘으면 그 경계에 붙인다.
//   세로는 선호 방향에 높이가 모자라고 반대쪽 여유가 더 크면 뒤집는다.
const placePopover = (anchorEl, popEl, placement) => {
  const rect = anchorEl.getBoundingClientRect();
  const width = popEl.offsetWidth;
  const height = popEl.offsetHeight;
  const [vertical, horizontal] = placement.split("-");

  let minLeft = POPOVER_VIEWPORT_MARGIN;
  let maxRight = window.innerWidth - POPOVER_VIEWPORT_MARGIN;
  const dialog = anchorEl.closest('[role="dialog"]');
  if (dialog) {
    const box = dialog.getBoundingClientRect();
    const dialogLeft = Math.max(minLeft, box.left + POPOVER_VIEWPORT_MARGIN);
    const dialogRight = Math.min(maxRight, box.right - POPOVER_VIEWPORT_MARGIN);
    if (dialogRight - dialogLeft >= width) {
      minLeft = dialogLeft;
      maxRight = dialogRight;
    }
  }
  const preferredLeft = horizontal === "end" ? rect.right - width : rect.left;
  const left = Math.max(minLeft, Math.min(preferredLeft, maxRight - width));

  const below =
    window.innerHeight - rect.bottom - POPOVER_GAP - POPOVER_VIEWPORT_MARGIN;
  const above = rect.top - POPOVER_GAP - POPOVER_VIEWPORT_MARGIN;
  const up =
    vertical === "top"
      ? !(above < height && below > above)
      : below < height && above > below;
  return up
    ? { left, bottom: window.innerHeight - rect.top + POPOVER_GAP }
    : { left, top: rect.bottom + POPOVER_GAP };
};

/**
 * DS Tooltip — figma "Tooltip-Dark" (이름과 달리 white bg).
 *   4334:7840 제목 + bullet 3줄 / 4334:7921 bullet 만 / 4111:132602 본문형(굵은 강조 포함)
 *   트리거 기본값 = ? 아이콘 20px Text/Disabled (4301:147930 icon_regular)
 *
 * 열림: 트리거 hover. 터치/클릭은 토글(고정) — 고정 상태는 바깥 클릭/ESC 로 해제.
 * 배치: body 포털 + position:fixed. 부모 기준 absolute 면 모달 본문·테이블 프레임의 overflow 에 잘리거나
 *   스크롤 폭을 늘린다. 기본 트리거 아래(gap 4), 좌표는 placePopover. 스크롤/리사이즈 시 재계산.
 *   포털은 React 트리로 이벤트가 전파되므로 팝오버 안 클릭도 트리거 조상의 onClick 에 닿는다.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.title] - SemiBold 16/26 Text/Primary
 * @param {Array<React.ReactNode>} [props.items] - bullet 줄 (Regular 14/22 Text/Secondary, 줄 간격 12)
 * @param {React.ReactNode} [props.content] - 본문형. 문단은 <p>, 강조는 <strong>
 * @param {"bottom-start"|"bottom-end"|"top-start"|"top-end"} [props.placement="bottom-start"]
 * @param {React.ReactNode} [props.children] - 커스텀 트리거. 없으면 ? 아이콘
 * @param {string} [props.className]
 */
const DSTooltip = ({
  title,
  items,
  content,
  placement = "bottom-start",
  children,
  className = "",
}) => {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapperRef = useRef(null);
  const popRef = useRef(null);
  const open = hover || pinned;

  // 팝오버가 커밋된 뒤(useLayoutEffect) 크기를 읽어 좌표를 정한다
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const update = () => {
      if (!wrapperRef.current || !popRef.current) return;
      setPos(placePopover(wrapperRef.current, popRef.current, placement));
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, placement]);

  useEffect(() => {
    if (!pinned) return undefined;
    const handleMouseDown = (e) => {
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setPinned(false);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setPinned(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [pinned]);

  return (
    <span
      ref={wrapperRef}
      className={`ds-tooltip ${className}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children ? (
        <span
          className="ds-tooltip__anchor"
          onClick={() => setPinned((p) => !p)}
          role="button"
          tabIndex={0}
        >
          {children}
        </span>
      ) : (
        <button
          type="button"
          className="ds-tooltip__trigger"
          onClick={() => setPinned((p) => !p)}
          aria-label="도움말"
          aria-expanded={open}
        >
          <CircleQuestionMark size={20} />
        </button>
      )}
      {open &&
        createPortal(
          <div
            ref={popRef}
            className="ds-tooltip__pop"
            // 첫 커밋은 좌표 계산 전 — 보이지 않게 두고 layout effect 가 pos 를 채운다
            style={pos || { visibility: "hidden", top: 0, left: 0 }}
            role="tooltip"
          >
            {title ? <div className="ds-tooltip__title">{title}</div> : null}
            {items && items.length ? (
              <ul className="ds-tooltip__items">
                {items.map((item, idx) => (
                  <li key={idx} className="ds-tooltip__item">
                    {item}
                  </li>
                ))}
              </ul>
            ) : null}
            {content ? (
              <div className="ds-tooltip__content">{content}</div>
            ) : null}
          </div>,
          document.body,
        )}
    </span>
  );
};

export default React.memo(DSTooltip);
